import { Component, OnDestroy } from '@angular/core';
import { TransferHttpService } from '../../services/transfer-http/transfer-http.service';
import { Router, ActivatedRoute } from '@angular/router';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { tap, take, switchMap } from 'rxjs/operators';
import { FacWasteDrums } from '../../../models';
import { BehaviorSubject, Subscription } from 'rxjs';
import { GetTextPipe } from '../../pipes';

import * as moment from 'moment-mini';
import { AimsCommonPopupEditorComponent, AimsCommonTableHeader, AimsCommonTablePermissions } from '@aims/common-components';
import { MatDialog } from '@angular/material/dialog';
import { WasteDrumsPopupComponent } from './qualityControl/waste-drums-popup/waste-drums-popup.component';

@Component({
  selector: 'view-waste-drums',
  templateUrl: './viewWasteDrums.component.html',
  providers: [GetTextPipe]

})

export class ViewWasteDrumsComponent implements OnDestroy {

 // AimsCommonTable setup
  tableHeader: AimsCommonTableHeader[] = [
    { key: 'edit', label: 'Edit', type: 'button', filter: false,
      events: { click: (element) => this.handleRowEdit(element), }, },
    { key: 'delete', label: 'Delete', type: 'button', filter: false,
      events: { click: (element) => this.handleRowDelete(element), }, },
    { key: 'wasteDrumNumber', label: 'Drum Number', type: 'string' },
    { key: 'containerTypeId', label: 'Container Type', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.containerTypeId } }  },
    { key: 'wasteTypeId', label: 'Waste Type', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.wasteTypeId } }  },
    { key: 'siteId', label: 'Site', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.siteId } } },
    { key: 'issuedDate', label: 'Issued Date', type: 'date' },
    { key: 'offSiteDate', label: 'Off-Site Date', type: 'date' },
    { key: 'accumulationDate', label: 'Accumulation Date', type: 'date' },
    { key: 'issuedById', label: 'Issued By', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.issuedById } } },
    { key: 'wasteCurrentStatusId', label: 'Status', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.wasteCurrentStatusId } } },
    { key: 'sealedDate', label: 'Sealed Date', type: 'date' },
    { key: 'sealedById', label: 'Sealed By', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.sealedById } } },
    { key: 'closedOutDate', label: 'Closed-Out Date', type: 'date' },
    { key: 'closedOutById', label: 'Closed-Out By', type: 'dynamic',  filterType: 'dynamic',
      events: { filterAndSortCompare: (term, el) => { term == el.closedOutById} } },
  ];
  tablePermissions: AimsCommonTablePermissions = {
    create: false,
    update: false,
    delete: false
  };


  constructor(public transferHttp: TransferHttpService,
    private router: Router,
    public permissions: PermissionsService,
    private dialog: MatDialog,
    public getTextPipe: GetTextPipe) {
  }

  //***************************
  // Variables
  //***************************

  user = this.transferHttp.getCache("user");
  emps = this.transferHttp.getCache('employeeDropdown');
  wasteContainerTypes = this.transferHttp.getSingle('ProjectSupport/GetWasteContainerTypes');
  wasteTypes = this.transferHttp.getSingle('ProjectSupport/GetWasteTypes');
  wasteStati = this.transferHttp.getSingle('ProjectSupport/GetWasteStatus');
  sites = this.transferHttp.getCache('sitesDropdown');

  //Spinner
  spinnerText: string = "Loading...";
  showSpinner: boolean = false;


  reload$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  //Main Grid
  drums = this.reload$.pipe(
    tap(() => {
      this.spinnerText = "Loading...";
      this.showSpinner = true;
    }),
    switchMap(() => this.transferHttp.getSingle('ProjectSupport/ViewWasteDrums')),
    tap(() => this.showSpinner = false)
  );

  isNewDrum: boolean = false;
  drumToEdit: any;

  //***************************
  // Functions
  //***************************

  handleRowEdit(element) {
    let drumToEdit = element as WasteDrum;

    this.openPopup(drumToEdit, false);
  }

  handleRowAdd() {
    let drumToEdit = new WasteDrum(this.user, this.getTextPipe.transform(this.user.defaultSiteId, 'siteId', 'siteCode', this.sites));
    this.openPopup(drumToEdit, true);
  }

  handleRowDelete(element) {
    if (confirm(`Are you sure you want to PERMANENTLY delete drum ${element.wasteDrumNumber}?`)) {
      this.spinnerText = "Deleting...";
      this.transferHttp.delete(`ProjectSupport/DeleteDrum?drumId=${element.wasteDrumId}`).pipe(take(1)).subscribe(ret => {
        this.reload();
      });
    }
  }

  openPopup(drumToEdit: WasteDrum, isNew: boolean) {
    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
      data: {
        header: 'Waste Drum Details',
        isNew: isNew,
        content: WasteDrumsPopupComponent,
        contentData: { drumToEdit: drumToEdit, emps: this.emps, wasteContainerTypes: this.wasteContainerTypes, wasteTypes: this.wasteTypes,
            sites: this.sites, wasteStati: this.wasteStati, getTextPipe: this.getTextPipe  }
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (dialogResult) {
        drumToEdit.wasteDrumNumber = drumToEdit.wasteDrumNumber.substring(0,7) + dialogResult.wasteDrumNumberLastFive;
        drumToEdit.containerTypeId = dialogResult.containerTypeId;
        drumToEdit.wasteTypeId = dialogResult.wasteTypeId;
        drumToEdit.siteId = dialogResult.siteId;
        drumToEdit.issuedDate = dialogResult.issuedDate;
        drumToEdit.offSiteDate = dialogResult.offSiteDate;
        drumToEdit.accumulationDate = dialogResult.accumulationDate;
        drumToEdit.issuedById = dialogResult.issuedById;
        drumToEdit.wasteCurrentStatusId = dialogResult.wasteCurrentStatusId;
        drumToEdit.sealedDate = dialogResult.sealedDate;
        drumToEdit.sealedById = dialogResult.sealedById;
        drumToEdit.closedOutDate = dialogResult.closedOutDate;
        drumToEdit.closedOutById = dialogResult.closedOutById;

        this.drumSaveHandler(drumToEdit);
      } else {
        drumToEdit = null;
      }
    })
  }


  ////
  updateWasteDrumNumber(endFive: string, dataItem: FacWasteDrums) {
    dataItem.wasteDrumNumber = dataItem.wasteDrumNumber.substring(0, 7) + endFive;
  }

  updateWasteDrumNumberSite(siteId: number, dataItem: FacWasteDrums) {
    let siteCode = this.getTextPipe.transform(siteId, 'siteId', 'siteCode', this.sites);
    dataItem.wasteDrumNumber = siteCode + dataItem.wasteDrumNumber.substring(3);
  }

  reload() {
    this.reload$.next(true);
  }

  drumRemoveHandler({ dataItem }) {
    if (confirm(`Are you sure you want to PERMANENTLY delete drum ${dataItem.wasteDrumNumber}?`)) {
      this.spinnerText = "Deleting...";
      this.transferHttp.delete(`ProjectSupport/DeleteDrum?drumId=${dataItem.wasteDrumId}`).pipe(take(1)).subscribe(ret => {
        this.reload();
      });
    }
  }

  drumAddHandler(event) {
    this.isNewDrum = true;
    this.drumToEdit = new WasteDrum(this.user, this.getTextPipe.transform(this.user.defaultSiteId, 'siteId', 'siteCode', this.sites));
  }

  drumEditHandler({ dataItem, rowIndex }) {
    this.isNewDrum = false;
    this.drumToEdit = dataItem;
  }

  drumSaveHandler(drum: FacWasteDrums) {
    this.spinnerText = "Saving...";
    this.showSpinner = true;
    let sub$: Subscription = this.transferHttp.save('ProjectSupport/SaveDrum', drum, false).subscribe(show => {
      if (!show) {
        this.reload();
        sub$.unsubscribe();
      }
    })
    this.drumToEdit = undefined;
  }

  drumCancelHandler(resetValue: FacWasteDrums) {
    this.spinnerText = "Loading...";
    this.reload();
    this.drumToEdit = undefined;
  }

  updateAccumDate($event: Date, dataItem: FacWasteDrums) {
    dataItem.offSiteDate = moment($event).add(1, 'year').toDate();
  }

  //***************************
  // Init
  //***************************

  ngOnInit(): void {

  }

  ngOnDestroy() {
    this.reload$.complete();
  }

}

export class WasteDrum extends FacWasteDrums {
  constructor(user: any, siteCode: string) {
    super();
    this.siteId = user.defaultSiteId;
    this.wasteDrumNumber = `${siteCode}${new Date().getFullYear()}`;
    this.issuedDate = new Date();
    this.issuedById = user.employeeId;
    this.wasteCurrentStatusId = WasteDrumStatus.Filling;
    this.containerTypeId = null;
    this.wasteTypeId = null;
  }
}

const WasteDrumStatus = {
  Filling: 1
}
